import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template163: Día extra (Disneyland París + Versalles). Todo desde backend.
// page.texts[0]=header, [1]=intro | page.sections[0..1] (imageId, title, body, sabiasQue[0], tooltipBox.text, stickerImageId, llegar: { title, content }) | page.images, page.canvasImages (logo)
const Template163 = ({
	backgroundColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found) return found.imageUrl ?? found.url;
		}
		return undefined;
	};

	const headerText = page?.texts?.[0]?.content;
	const introText = page?.texts?.[1]?.content;
	const logoImage = getCanvasImageUrl("logo");

	const sections = Array.isArray(page?.sections) ? page.sections : [];
	const section0 = sections[0];
	const section1 = sections[1];

	const disneyImage = section0 ? getImageSrc(section0.imageId) : undefined;
	const disneylandStickerImage = section0
		? getImageSrc(section0.stickerImageId)
		: undefined;
	const versallesImage = section1 ? getImageSrc(section1.imageId) : undefined;
	const versallesStickerImage = section1
		? getImageSrc(section1.stickerImageId)
		: undefined;

	const actualHeaderColor = headerBackgroundColor || "#2C3E50";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: "#444",
		margin: 0,
	};

	const boxLlegar = {
		backgroundColor: "#E8F4F8",
		border: "3px solid #2C3E50",
		borderRadius: "12px",
		padding: "8px",
		fontSize: 12,
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		color: "#333",
		marginTop: "12px",
		marginBottom: isSmallScreen ? "12px" : 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{!isSmallScreen && logoImage && (
						<img
							src={logoImage}
							alt=""
							style={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{introText && (
				<Box data-block-id="text-1" sx={{ padding: "12px 12px 8px" }}>
					<Box component="p" sx={estiloP}>
						{introText}
					</Box>
				</Box>
			)}

			<Box
				sx={{
					padding: "0 12px 20px",
					display: "flex",
					flexDirection: "column",
					gap: "14px",
				}}
			>
				{section0 && (
					<>
						<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{disneyImage && (
								<img
									src={disneyImage}
									alt=""
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: 12,
									}}
								/>
							)}
							<Box sx={{ width: disneyImage ? "60%" : "100%" }}>
								{section0.title && (
									<Box component="h3" sx={estiloH3}>
										{section0.title}
									</Box>
								)}
								{section0.body && (
									<Box component="p" sx={estiloP}>
										{section0.body}
									</Box>
								)}
							</Box>
							{section0.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", bottom: 60, left: "33%" }}>
									<SabiasQue
										icono={section0.sabiasQue[0].icono}
										texto={section0.sabiasQue[0].texto}
									/>
								</Box>
							)}
							{disneylandStickerImage && (
								<img
									src={disneylandStickerImage}
									alt=""
									style={{
										width: 100,
										position: "absolute",
										bottom: isSmallScreen ? -80 : -50,
										right: -10,
									}}
								/>
							)}
							{section0.tooltipBox?.text && (
								<Box
									sx={{
										position: "absolute",
										bottom: isSmallScreen ? -70 : -20,
										right: 80,
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 0px 16px",
										padding: "10px 12px",
										fontSize: 11,
										fontFamily: `${
											fontFamily || "'Poppins', sans-serif"
										} !important`,
										color: "#444",
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: 250,
									}}
								>
									{section0.tooltipBox.text}
								</Box>
							)}
						</Box>

						{section0.llegar?.title && (
							<Box
								data-block-id="text-3"
								sx={{
									...boxLlegar,
									marginTop: isSmallScreen ? "60px" : "12px",
								}}
							>
								<Box sx={{ paddingLeft: "4px", margin: 0, lineHeight: 1.5 }}>
									<Box component="h3" sx={{ ...estiloH3, marginBottom: "8px" }}>
										{section0.llegar.title}
									</Box>
									{section0.llegar.content && (
										<Box
											component="div"
											dangerouslySetInnerHTML={{
												__html: section0.llegar.content,
											}}
											sx={estiloP}
										/>
									)}
								</Box>
							</Box>
						)}
					</>
				)}

				{section1 && (
					<>
						<Box data-block-id="text-4" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{versallesImage && (
								<img
									src={versallesImage}
									alt=""
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: 12,
									}}
								/>
							)}
							<Box sx={{ width: versallesImage ? "60%" : "100%" }}>
								{section1.title && (
									<Box component="h3" sx={estiloH3}>
										{section1.title}
									</Box>
								)}
								{section1.body && (
									<Box component="p" sx={estiloP}>
										{section1.body}
									</Box>
								)}
							</Box>
							{section1.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", bottom: 35, left: "33%" }}>
									<SabiasQue
										icono={section1.sabiasQue[0].icono}
										texto={section1.sabiasQue[0].texto}
									/>
								</Box>
							)}
							{versallesStickerImage && (
								<img
									src={versallesStickerImage}
									alt=""
									style={{
										width: 100,
										position: "absolute",
										bottom: isSmallScreen ? -90 : -50,
										right: -10,
									}}
								/>
							)}
							{section1.tooltipBox?.text && (
								<Box
									sx={{
										position: "absolute",
										bottom: isSmallScreen ? -70 : -30,
										right: 80,
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 0px 16px",
										padding: "10px 12px",
										fontSize: 11,
										fontFamily: `${
											fontFamily || "'Poppins', sans-serif"
										} !important`,
										color: "#444",
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: 250,
									}}
								>
									{section1.tooltipBox.text}
								</Box>
							)}
						</Box>

						{section1.llegar?.title && (
							<Box
								data-block-id="text-5"
								sx={{
									...boxLlegar,
									marginTop: isSmallScreen ? "60px" : "12px",
								}}
							>
								<Box sx={{ paddingLeft: "4px", margin: 0, lineHeight: 1.5 }}>
									<Box component="h3" sx={{ ...estiloH3, marginBottom: "8px" }}>
										{section1.llegar.title}
									</Box>
									{section1.llegar.content && (
										<Box
											component="div"
											dangerouslySetInnerHTML={{
												__html: section1.llegar.content,
											}}
											sx={estiloP}
										/>
									)}
								</Box>
							</Box>
						)}
					</>
				)}
			</Box>
		</Box>
	);
};

Template163.thumbnail = "/images/template-1.png";

export default Template163;
